<script setup lang="ts">
import { cloneDeep } from "lodash";
import InputSwitch from "primevue/inputswitch";
import MultiSelect from "primevue/multiselect";
import { computed, defineEmits, defineProps, ref } from "vue";
import { programStatuses } from "@/components/program-manager/create-edit/utils";
import { useGetEntitiesData } from "@/components/program-manager/sessions/composable.js";
import { exchangeSessionType, sessionTypes } from "@/components/program-manager/sessions/constants";
import BaseSearchBarFilterModal from "@/components/shared/BaseSearchBarFilterModal.vue";
import { useGetGroupedCitiesData } from "@/providers/cities/useGetGroupedCitiesData.ts";
import { useGetTermsData } from "@/providers/terms/useGetTermsData";
import { baseFilters, MARKETPLACE_STATUS } from "./constants.ts";
import { programsFilterModalPresets } from "./style.ts";

const props = defineProps({
  isSharedItemsTab: {
    type: Boolean,
    required: true,
  },
});

const filters = ref(cloneDeep(baseFilters));
const emit = defineEmits(["updateFilters"]);

const sessionTypesFilter = ref([...sessionTypes, exchangeSessionType]);

const { data: termsData } = useGetTermsData({});
const { data: entitiesData } = useGetEntitiesData({ limit: 1000, page: 1, q: "" });
const { data: citiesData } = useGetGroupedCitiesData({ limit: 1000, page: 1, in_use: true });

const isPublic = computed(() => filters.value.marketplace === true);
const isPrivate = computed(() => filters.value.marketplace === "false");

const updateMarketplace = (selection) => {
  if (selection === MARKETPLACE_STATUS.PUBLIC) {
    filters.value.marketplace = isPublic.value ? undefined : true;
  } else if (selection === MARKETPLACE_STATUS.PRIVATE) {
    filters.value.marketplace = isPrivate.value ? undefined : "false";
  }
};

const applyFilters = () => {
  emit("updateFilters", filters.value);
};

const clearFilters = () => {
  filters.value = cloneDeep(baseFilters);
  emit("updateFilters", filters.value);
};
</script>

<template>
  <BaseSearchBarFilterModal
    title="Program Filter"
    @apply-filters="applyFilters"
    @clear-filters="clearFilters"
  >
    <template #default="{ isModalOpen }">
      <div class="flex flex-col gap-6">
        <div class="w-full">
          <span class="block text-sm font-semibold text-gray-700">Session Type(s)</span>
          <MultiSelect
            v-model="filters.type_ids"
            :options="sessionTypesFilter"
            placeholder="Pick a value"
            optionLabel="name"
            option-value="id"
            display="chip"
            filter
            :pt="programsFilterModalPresets.multiselect"
          />
        </div>
        <div class="w-full">
          <span class="block text-sm font-semibold text-gray-700">Term(s)</span>
          <MultiSelect
            v-model="filters.term_ids"
            :options="termsData"
            placeholder="Pick a value"
            optionLabel="name"
            option-value="id"
            display="chip"
            filter
            :pt="programsFilterModalPresets.multiselect"
          />
        </div>
        <div class="w-full">
          <span class="block text-sm font-semibold text-gray-700">University</span>
          <MultiSelect
            v-model="filters.host_institution_ids"
            :options="entitiesData"
            placeholder="Pick a value"
            optionLabel="name"
            option-value="id"
            display="chip"
            filter
            :pt="programsFilterModalPresets.multiselect"
          />
        </div>
        <div v-if="!isSharedItemsTab" class="w-full">
          <span class="block text-sm font-semibold text-gray-700">Status</span>
          <MultiSelect
            v-model="filters.status_ids"
            :options="programStatuses"
            placeholder="Pick a value"
            optionLabel="name"
            option-value="id"
            display="chip"
            filter
            :pt="programsFilterModalPresets.multiselect"
          />
        </div>
        <div class="w-full">
          <span class="block text-sm font-semibold text-gray-700">Cities</span>
          <MultiSelect
            v-model="filters.city_ids"
            :options="citiesData"
            optionValue="value"
            optionLabel="label"
            filter
            optionGroupLabel="label"
            optionGroupChildren="items"
            display="chip"
            placeholder="Pick a value"
            :pt="programsFilterModalPresets.multiselect"
          />
        </div>
        <div v-if="!isSharedItemsTab" class="flex flex-col gap-6">
          <div class="w-full flex items-center gap-2">
            <span class="block text-sm font-semibold text-gray-700 w-52">Show only Public Programs</span>
            <InputSwitch
              v-model="isPublic"
              @update:model-value="updateMarketplace(MARKETPLACE_STATUS.PUBLIC)"
              :pt="programsFilterModalPresets.inputswitch"
            />
          </div>
          <div class="w-full flex items-center gap-2">
            <span class="block text-sm font-semibold text-gray-700 w-52">Show only Private Programs </span>
            <InputSwitch
              v-model="isPrivate"
              @update:model-value="updateMarketplace(MARKETPLACE_STATUS.PRIVATE)"
              :pt="programsFilterModalPresets.inputswitch"
            />
          </div>
        </div>
      </div>
    </template>
  </BaseSearchBarFilterModal>
</template>
